import { ChevronLeft, ChevronRight } from "@cynthai/icons";
import { formatNumber } from "@cynthai/shared";
import * as ReactPaginateModule from "react-paginate";
import { twMerge } from "tailwind-merge";

import { resolveReactPaginate } from "./pagination-utils";

type ReactPaginateComponent = NonNullable<typeof ReactPaginateModule.default>;

let resolvedReactPaginate: ReactPaginateComponent | null | undefined;

function getReactPaginate() {
  if (resolvedReactPaginate !== undefined) {
    return resolvedReactPaginate;
  }

  try {
    resolvedReactPaginate = resolveReactPaginate<ReactPaginateComponent>(
      ReactPaginateModule.default,
    );
  } catch (error) {
    console.error("Failed to resolve react-paginate component:", error);
    resolvedReactPaginate = null;
  }

  return resolvedReactPaginate;
}

type PaginationProps = {
  totalPages: number;
  value: number; // 0-indexed
  onChange: (value: number) => void;
  pageSize: number;
  totalItems: number;
  className?: string;
};

export default function Pagination({
  totalPages,
  value,
  onChange,
  pageSize,
  totalItems,
  className,
}: Readonly<PaginationProps>) {
  const ReactPaginate = totalPages > 0 ? getReactPaginate() : null;

  return (
    <div
      className={twMerge(
        `bg-secondary text-text-secondary flex items-center justify-between rounded-lg px-6 py-3 text-sm`,
        className,
      )}
    >
      <span>
        {formatNumber(value * pageSize + 1)}-
        {formatNumber(Math.min((value + 1) * pageSize, totalItems))} of{" "}
        {formatNumber(totalItems)} items
      </span>
      {totalPages > 0 && ReactPaginate && (
        <ReactPaginate
          breakLabel="..."
          forcePage={value}
          onPageChange={(selectedItem: { selected: number }) => {
            onChange(selectedItem.selected);
          }}
          pageRangeDisplayed={3}
          pageCount={totalPages}
          previousLabel={<ChevronLeft />}
          nextLabel={<ChevronRight />}
          renderOnZeroPageCount={null}
          className="text-text-tertiary flex flex-row gap-2"
          nextLinkClassName="text-text-secondary cursor-pointer min-w-7 size-7 rounded-sm bg-secondary flex justify-center items-center"
          previousLinkClassName="text-text-secondary cursor-pointer min-w-7 size-7 rounded-sm bg-secondary flex justify-center items-center"
          pageLinkClassName="cursor-pointer min-w-8 px-2 h-7 rounded-sm flex justify-center items-center"
          breakLinkClassName="cursor-pointer min-w-4 px-2 h-7 rounded-sm flex justify-center items-center"
          activeLinkClassName="text-text-button bg-primary"
          pageLabelBuilder={(page: number) => formatNumber(page)}
        />
      )}
    </div>
  );
}
